// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { setFilterDrawerIsOpen } from "@sc/App/Handlers/XNG/ProductList";
import { CustomAppState } from "@sc/App/Store/Reducers";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    view: state.pages.productList.view,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    filterDrawerIsOpen: state.xProductList.filterDrawerIsOpen,
    productFilters: state.pages.productList.productFilters,
});

// XNG-Changes: add mapDispatchToProps
const mapDispatchToProps = {
    setFilterDrawerIsOpen,
    clearAllProductFilters,
};

// XNG-Changes: add mapDispatchToProps to props type
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    // XNG-Changes
    filtersDrawer?: DrawerPresentationProps;
    filterWrapper?: InjectableCss;
    controlsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, view, filterDrawerIsOpen, setFilterDrawerIsOpen }) => {
    // const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const leftColumnGridItemStyle = styles.leftColumnGridItem;
    // const rightColumnGridItemStyle =
    //     view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;
    const rightColumnGridItemStyle = styles.rightColumnGridItem;

    // <XNG-Changes>
    const handleCloseFilterDrawer = () => {
        setFilterDrawerIsOpen({ filterDrawerIsOpen: false });
    };
    const handleClearFilterDrawerValues = () => {
        clearAllProductFilters();
        handleCloseFilterDrawer();
    };
    // </XNG-Changes>

    return (
        <StyledWrapper {...styles.container}>
            {/* <GridContainer {...styles.container}> */}
            {/* <GridItem {...leftColumnGridItemStyle}> */}
            <StyledWrapper {...leftColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content00" />
            </StyledWrapper>
            <StyledWrapper {...rightColumnGridItemStyle}>
                {/* </GridItem> */}
                {/* <GridItem {...rightColumnGridItemStyle}> */}
                <Zone contentId={id} zoneName="Content01" />
            </StyledWrapper>
            {/* </GridItem> */}
            {/* XNG-Changes: add drawer for tablet & mobile screens */}
            <Drawer
                isOpen={filterDrawerIsOpen}
                handleClose={handleCloseFilterDrawer}
                position="left"
                draggable={false}
                contentLabel="Refine drawer"
                headline={translate("Filter By")}
                {...styles.filtersDrawer}
            >
                <StyledWrapper {...styles.filterWrapper}>
                    <StyledWrapper {...styles.controlsWrapper}>
                        {createWidgetElement("ProductList/ProductListSortSelect", { fields: {} })}
                        {createWidgetElement("ProductList/ProductListPagination", {
                            fields: { showPagesNumber: false },
                        })}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Content00" />
                    {createWidgetElement("ProductList/ProductListFilterList", {
                        fields: {
                            viewType: "title",
                        },
                        showAsButton: true,
                    } as any)}
                </StyledWrapper>
            </Drawer>
            {/* </GridContainer> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: add mapDispatchToProps
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
